import {
  createSportsnewPlacementKey,
  createSportsnewPlacementChallenge,
  fingerprintSportsnewPlacementCommand,
} from './sportsnewPlacementAttemptIdentity.js';
import {
  sportsnewPlacementPersistenceError,
  sportsnewPlacementUnresolvedError,
  validateSportsnewPlacementAttempt,
} from './sportsnewPlacementAttemptContract.js';
import {
  canReuseSportsnewPlacementAttempt,
  classifySportsnewPlacementReceiptIssueFailure,
  classifySportsnewPlacementResponse,
  isSportsnewPlacementContinuationState,
  refreshSportsnewPlacementAttempt,
  SPORTSNEW_PLACEMENT_ATTEMPT_STATE,
  SPORTSNEW_PLACEMENT_RECEIPT_ISSUE_DECISION,
  SPORTSNEW_PLACEMENT_RESPONSE_DECISION,
  transitionSportsnewPlacementAttempt,
} from './sportsnewPlacementAttemptPolicy.js';
import {
  createSportsnewPlacementAttemptStore,
  SPORTSNEW_PLACEMENT_ATTEMPT_MAX_PER_USER,
} from './sportsnewPlacementAttemptStore.js';

export const SPORTSNEW_PLACEMENT_ATTEMPT_TTL_MS = 10 * 60 * 1000;

const attemptResult = (attempt, reused) => Object.freeze({
  userId: attempt.userId,
  idempotencyKey: attempt.idempotencyKey,
  challenge: attempt.challenge,
  accountGeneration: attempt.accountGeneration,
  commandFingerprint: attempt.commandFingerprint,
  placementReceipt: attempt.placementReceipt,
  receiptExpiresAtMs: attempt.receiptExpiresAtMs,
  phase: attempt.phase,
  surface: attempt.surface,
  reused,
  state: attempt.state,
  continuation: reused && isSportsnewPlacementContinuationState(attempt.state),
});

export const createSportsnewPlacementAttemptOwner = ({
  clock = () => Date.now(),
  ttlMs = SPORTSNEW_PLACEMENT_ATTEMPT_TTL_MS,
  store = createSportsnewPlacementAttemptStore(),
  createKey = createSportsnewPlacementKey,
  createChallenge = createSportsnewPlacementChallenge,
  fingerprintCommand = fingerprintSportsnewPlacementCommand,
  maxAttemptsPerUser = SPORTSNEW_PLACEMENT_ATTEMPT_MAX_PER_USER,
} = {}) => {
  const memoryByUser = new Map();
  const hydratedUsers = new Set();
  const persistenceBlockedUsers = new Set();
  const acquisitionTails = new Map();

  const normalizedUserId = value => {
    const userId = Number(value);
    return Number.isSafeInteger(userId) && userId > 0 ? userId : null;
  };
  const blockPersistence = userId => {
    persistenceBlockedUsers.add(userId);
    throw sportsnewPlacementPersistenceError();
  };
  const userAttempts = userId => {
    let attempts = memoryByUser.get(userId);
    if (!attempts) {
      attempts = new Map();
      memoryByUser.set(userId, attempts);
    }
    return attempts;
  };
  const hydrate = (userIdValue, legacyFingerprint = null) => {
    const userId = normalizedUserId(userIdValue);
    if (!userId) return new Map();
    if (persistenceBlockedUsers.has(userId)) throw sportsnewPlacementPersistenceError();
    if (hydratedUsers.has(userId)) {
      const attempts = userAttempts(userId);
      if (attempts.size === 0 && legacyFingerprint != null) {
        let claimed;
        try {
          claimed = store.claimLegacy?.(userId, legacyFingerprint) ?? null;
        } catch {
          return blockPersistence(userId);
        }
        if (claimed) {
          const loaded = validateSportsnewPlacementAttempt(claimed);
          if (!loaded) return blockPersistence(userId);
          const upgraded = loaded.version === 1 ? Object.freeze({
            ...loaded,
            version: 2,
            phase: 'RESOLUTION',
            challenge: createChallenge(),
            surface: 'SPORTSNEW_BET_CART',
          }) : loaded;
          if (loaded.version === 1 && !store.write(upgraded)) return blockPersistence(userId);
          attempts.set(upgraded.fingerprint, upgraded);
        }
      }
      return attempts;
    }
    let storedRows;
    try {
      storedRows = typeof store.readAll === 'function'
        ? store.readAll(userId)
        : [store.read?.(userId)].filter(Boolean);
      if (storedRows.length === 0 && legacyFingerprint != null) {
        const claimed = store.claimLegacy?.(userId, legacyFingerprint) ?? null;
        if (claimed) storedRows = [claimed];
      }
    } catch {
      return blockPersistence(userId);
    }
    const attempts = userAttempts(userId);
    for (const stored of storedRows) {
      const loaded = validateSportsnewPlacementAttempt(stored);
      if (!loaded) return blockPersistence(userId);
      const upgraded = loaded.version === 1 ? Object.freeze({
        ...loaded,
        version: 2,
        phase: 'RESOLUTION',
        challenge: createChallenge(),
        surface: 'SPORTSNEW_BET_CART',
      }) : loaded;
      if (loaded.version === 1 && !store.write(upgraded)) return blockPersistence(userId);
      attempts.set(upgraded.fingerprint, upgraded);
    }
    if (attempts.size > maxAttemptsPerUser) return blockPersistence(userId);
    hydratedUsers.add(userId);
    return attempts;
  };
  const persistAttempt = attempt => {
    const userId = normalizedUserId(attempt?.userId);
    if (!userId || persistenceBlockedUsers.has(userId)) return false;
    try {
      if (!store.write(attempt)) {
        persistenceBlockedUsers.add(userId);
        return false;
      }
    } catch {
      persistenceBlockedUsers.add(userId);
      return false;
    }
    userAttempts(userId).set(attempt.fingerprint, attempt);
    return true;
  };
  const removeAttempt = attempt => {
    const userId = normalizedUserId(attempt?.userId);
    if (!userId || persistenceBlockedUsers.has(userId)) return false;
    try {
      if (!store.remove(userId, attempt.fingerprint)) {
        persistenceBlockedUsers.add(userId);
        return false;
      }
    } catch {
      persistenceBlockedUsers.add(userId);
      return false;
    }
    userAttempts(userId).delete(attempt.fingerprint);
    return true;
  };
  const refreshExact = attempt => {
    const refreshed = refreshSportsnewPlacementAttempt(attempt, Number(clock()));
    if (refreshed && refreshed !== attempt && !persistAttempt(refreshed)) {
      throw sportsnewPlacementPersistenceError();
    }
    return refreshed;
  };
  const listForUser = userIdValue => {
    const userId = normalizedUserId(userIdValue);
    if (!userId) return [];
    const attempts = hydrate(userId);
    return [...attempts.values()]
      .sort((left, right) => left.fingerprint.localeCompare(right.fingerprint))
      .map(refreshExact);
  };
  const findByKey = (idempotencyKey, userIdValue, challenge) => {
    const requestedUserId = normalizedUserId(userIdValue);
    if (!requestedUserId) return null;
    return listForUser(requestedUserId).find(attempt => (
      attempt?.idempotencyKey === idempotencyKey
      && attempt?.challenge === challenge
      && attempt?.surface === 'SPORTSNEW_BET_CART'
    )) ?? null;
  };
  const acquireOnce = async ({ userId: rawUserId, accountGeneration: rawGeneration, payload }) => {
    const userId = normalizedUserId(rawUserId);
    const accountGeneration = Number(rawGeneration);
    if (!userId || persistenceBlockedUsers.has(userId)) throw sportsnewPlacementPersistenceError();
    if (!Number.isSafeInteger(accountGeneration) || accountGeneration <= 0) {
      throw sportsnewPlacementPersistenceError();
    }
    const now = Number(clock());
    const attempts = hydrate(userId);
    // The UI lifecycle may restart, but admitted receipt generations are
    // immutable. Snapshot before the first digest await: reconciliation can
    // retire a row or replace its receipt while this acquire is suspended.
    const snapshot = [...attempts.values()];
    const fingerprints = new Map();
    const fingerprintFor = generation => {
      if (!fingerprints.has(generation)) {
        fingerprints.set(generation, fingerprintCommand({
          userId, accountGeneration: generation, payload,
        }));
      }
      return fingerprints.get(generation);
    };
    const fingerprint = await fingerprintFor(accountGeneration);
    if (snapshot.length === 0) {
      // Keep the existing exact legacy ownership claim; never guess an owner
      // or silently discard a generation-less unresolved record.
      snapshot.push(...hydrate(userId, fingerprint).values());
    }
    const matching = [];
    for (const stored of snapshot) {
      if (stored.version !== 3) throw sportsnewPlacementUnresolvedError();
      if (stored.fingerprint === await fingerprintFor(stored.accountGeneration)) {
        matching.push(stored);
      }
    }
    if (persistenceBlockedUsers.has(userId)) throw sportsnewPlacementPersistenceError();
    if (matching.length > 1) throw sportsnewPlacementUnresolvedError();
    if (matching.length === 1) {
      const stored = matching[0];
      // All mutations replace immutable objects. Equality includes receipt,
      // phase, fingerprint and generation, not just key/challenge/state.
      if (attempts.get(stored.fingerprint) !== stored) throw sportsnewPlacementUnresolvedError();
      const current = refreshExact(stored);
      if (canReuseSportsnewPlacementAttempt(current, {
        userId, fingerprint: stored.fingerprint, accountGeneration: stored.accountGeneration,
      })) return attemptResult(current, true);
      throw sportsnewPlacementUnresolvedError();
    }
    if (attempts.has(fingerprint) || attempts.size >= maxAttemptsPerUser) throw sportsnewPlacementUnresolvedError();
    const attempt = Object.freeze({
      version: 3,
      idempotencyKey: createKey(),
      fingerprint,
      userId,
      state: SPORTSNEW_PLACEMENT_ATTEMPT_STATE.IN_FLIGHT,
      phase: 'INITIAL',
      challenge: createChallenge(),
      surface: 'SPORTSNEW_BET_CART',
      createdAt: now,
      updatedAt: now,
      expiresAt: now + Number(ttlMs),
      accountGeneration,
      commandFingerprint: null,
      placementReceipt: null,
      receiptExpiresAtMs: null,
    });
    if (!persistAttempt(attempt)) throw sportsnewPlacementPersistenceError();
    return attemptResult(attempt, false);
  };

  return Object.freeze({
    acquire(input) {
      const userId = normalizedUserId(input?.userId);
      const tail = acquisitionTails.get(userId) ?? Promise.resolve();
      const result = tail.then(() => acquireOnce(input));
      acquisitionTails.set(userId, result.catch(() => {}));
      return result;
    },
    list(userId) {
      if (persistenceBlockedUsers.has(Number(userId))) throw sportsnewPlacementPersistenceError();
      return listForUser(userId);
    },
    current(userId, identity = {}) {
      const attempts = listForUser(userId);
      if (identity?.idempotencyKey || identity?.challenge || identity?.fingerprint) {
        return attempts.find(attempt => (
          (!identity.idempotencyKey || attempt.idempotencyKey === identity.idempotencyKey)
          && (!identity.challenge || attempt.challenge === identity.challenge)
          && (!identity.fingerprint || attempt.fingerprint === identity.fingerprint)
          && (!identity.accountGeneration
            || Number(attempt.accountGeneration) === Number(identity.accountGeneration))
        )) ?? null;
      }
      return attempts[0] ?? null;
    },
    authorizeContinuation(idempotencyKey, userId, challenge) {
      const current = findByKey(idempotencyKey, userId, challenge);
      if (!current) throw sportsnewPlacementUnresolvedError();
      if (!isSportsnewPlacementContinuationState(current.state)) throw sportsnewPlacementUnresolvedError();
      return Object.freeze({
        userId: current.userId,
        idempotencyKey: current.idempotencyKey,
        state: current.state,
        challenge: current.challenge,
        phase: 'RECOVER',
        surface: current.surface,
        accountGeneration: current.accountGeneration,
        commandFingerprint: current.commandFingerprint,
        placementReceipt: current.placementReceipt,
        receiptExpiresAtMs: current.receiptExpiresAtMs,
      });
    },
    recordReceipt(idempotencyKey, receiptData, userId, challenge, accountGeneration) {
      const current = findByKey(idempotencyKey, userId, challenge);
      if (!current || current.version !== 3) return false;
      if (Number(current.accountGeneration) !== Number(accountGeneration)) return false;
      if (
        typeof receiptData?.receipt !== 'string'
        || !receiptData.receipt
        || !/^[0-9a-f]{64}$/i.test(String(receiptData.commandFingerprint || ''))
        || Number(receiptData.accountGeneration) !== Number(current.accountGeneration)
        || !Number.isFinite(Number(receiptData.expiresAtMs))
      ) return false;
      return persistAttempt(Object.freeze({
        ...current,
        commandFingerprint: receiptData.commandFingerprint,
        placementReceipt: receiptData.receipt,
        receiptExpiresAtMs: Number(receiptData.expiresAtMs),
        updatedAt: Number(clock()),
      }));
    },
    recordReceiptIssueFailure(idempotencyKey, response, options = {}, userId, challenge) {
      const current = findByKey(idempotencyKey, userId, challenge);
      if (!current) {
        return Object.freeze({ retained: false, retryReceiptIssue: false, persisted: false });
      }
      const definiteUnadmitted = options.receiptIssued !== true
        && options.placementRequestStarted !== true
        && classifySportsnewPlacementReceiptIssueFailure(response)
          === SPORTSNEW_PLACEMENT_RECEIPT_ISSUE_DECISION.RETIRE_UNADMITTED;
      if (definiteUnadmitted) {
        const persisted = removeAttempt(current);
        return Object.freeze({ retained: false, retryReceiptIssue: false, persisted });
      }
      const transitioned = transitionSportsnewPlacementAttempt(
        current,
        SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN,
        Number(clock()),
        ttlMs,
      );
      const persisted = persistAttempt(Object.freeze({ ...transitioned, phase: 'RESOLUTION' }));
      return Object.freeze({ retained: true, retryReceiptIssue: persisted, persisted });
    },
    recordResponse(idempotencyKey, response, options = {}, userId, challenge) {
      const current = findByKey(idempotencyKey, userId, challenge);
      if (!current) return false;
      const decision = classifySportsnewPlacementResponse(response, options);
      const transitioned = transitionSportsnewPlacementAttempt(current, decision, Number(clock()), ttlMs);
      const next = transitioned ? Object.freeze({
        ...transitioned,
        phase: decision === SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_CONTINUATION
          ? 'RECOVER'
          : 'RESOLUTION',
      }) : null;
      const persisted = next ? persistAttempt(next) : removeAttempt(current);
      return Object.freeze({
        retained: decision !== SPORTSNEW_PLACEMENT_RESPONSE_DECISION.RETIRE,
        continuation: decision === SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_CONTINUATION,
        persisted,
      });
    },
    markUnknown(idempotencyKey, userId, challenge) {
      const current = findByKey(idempotencyKey, userId, challenge);
      if (!current) return false;
      const transitioned = transitionSportsnewPlacementAttempt(
        current,
        SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN,
        Number(clock()),
        ttlMs,
      );
      return persistAttempt(Object.freeze({ ...transitioned, phase: 'RESOLUTION' }));
    },
    retire(idempotencyKey, userId, challenge) {
      const current = findByKey(idempotencyKey, userId, challenge);
      return current ? removeAttempt(current) : false;
    },
  });
};
